import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DEFAULT_MENU_CONFIG } from '../lib/menuConfig';

const ContentContext = createContext(null);

// Hardcoded fallback so the site always renders even if API is down
const FALLBACK_CONTENT = {
  translations: null,  // LanguageContext has its own hardcoded fallback
  hero: {
    slides: [
      {
        id: 'slide-1',
        overline_en: 'Established 1987 — A Legacy of Excellence',
        overline_ar: 'تأسست عام 1987',
        title_highlight_en: 'A Legacy of',
        title_highlight_ar: 'إرث من',
        title_main_en: 'Royal Excellence',
        title_main_ar: 'التميز الملكي',
        description_en: 'Where tradition meets perfection, crafting moments of pure distinction for our discerning clientele worldwide.',
        description_ar: 'حيث يلتقي التراث بالكمال، نصنع لحظات من التميز الفريد لعملائنا المميزين حول العالم.',
        cta_en: 'Explore Our Legacy',
        cta_ar: 'اكتشف إرثنا',
        cta_target: 'about',
        image_url: '/uploads/df41f490-c8fe-4336-8f0c-7d809a34ba0a.png',
        enabled: true,
        order: 1,
      },
      {
        id: 'slide-2',
        overline_en: 'Unmatched Quality',
        overline_ar: 'جودة لا تضاهى',
        title_highlight_en: 'Master',
        title_highlight_ar: 'حرفية',
        title_main_en: 'Craftsmanship',
        title_main_ar: 'الخبراء',
        description_en: 'Every product is meticulously crafted by master artisans with decades of expertise and passion.',
        description_ar: 'كل منتج مصنوع بدقة على أيدي حرفيين ماهرين بخبرة تمتد لعقود من الزمن.',
        cta_en: 'View Our Products',
        cta_ar: 'تعرف على منتجاتنا',
        cta_target: 'products',
        image_url: '/uploads/4abda999-29c2-4f4c-89cf-5532391a773c.png',
        enabled: true,
        order: 2,
      },
      {
        id: 'slide-3',
        overline_en: 'Global Presence',
        overline_ar: 'انتشار عالمي',
        title_highlight_en: 'Serving',
        title_highlight_ar: 'نخدم',
        title_main_en: '25+ Countries',
        title_main_ar: '25+ دولة',
        description_en: 'From the heart of Dubai to the world, delivering premium tobacco products to global markets.',
        description_ar: 'من قلب دبي إلى العالم، نقدم أفضل منتجات التبغ للأسواق العالمية.',
        cta_en: 'Contact Us',
        cta_ar: 'تواصل معنا',
        cta_target: 'contact',
        image_url: '/uploads/9a42e3f1-cacf-4315-89d5-58f9a9d4bdcb.png',
        enabled: true,
        order: 3,
      },
    ],
    autoplay_interval_ms: 6000,
    badge: {
      number: '37+',
      label_en: 'Years of Excellence',
      label_ar: 'عاماً من التميز',
      enabled: true,
    },
  },
  products: {
    categories: [
      { id: 'premium', key: 'premium', icon: 'crown', title_en: 'Royal Crown Series', title_ar: 'سلسلة التاج الملكي', description_en: 'Our flagship collection featuring rare and aged tobacco blends for the ultimate connoisseur.', description_ar: 'مجموعتنا الرئيسية تضم خلطات تبغ نادرة ومعتقة لعشاق التميز.', modal_content_en: '', modal_content_ar: '', image_url: '/uploads/df41f490-c8fe-4336-8f0c-7d809a34ba0a.png', featured: true, enabled: true, order: 1 },
      { id: 'classic', key: 'classic', icon: 'leaf', title_en: 'Heritage Collection', title_ar: 'مجموعة التراث', description_en: 'Time-tested recipes passed down through generations of master blenders.', description_ar: 'وصفات خالدة توارثتها أجيال من خبراء المزج الماهرين.', modal_content_en: '', modal_content_ar: '', image_url: '/uploads/4abda999-29c2-4f4c-89cf-5532391a773c.png', featured: false, enabled: true, order: 2 },
      { id: 'light', key: 'light', icon: 'wind', title_en: 'Velvet Edition', title_ar: 'إصدار المخمل', description_en: 'Elegantly smooth blends crafted for those who appreciate subtle refinement.', description_ar: 'خلطات ناعمة وأنيقة لمن يقدرون الرقي الرفيع.', modal_content_en: '', modal_content_ar: '', image_url: '/uploads/9a42e3f1-cacf-4315-89d5-58f9a9d4bdcb.png', featured: false, enabled: true, order: 3 },
      { id: 'special', key: 'special', icon: 'star', title_en: 'Reserve Selection', title_ar: 'التشكيلة المحدودة', description_en: 'Exclusive limited releases featuring exotic blends from around the world.', description_ar: 'إصدارات حصرية محدودة تضم خلطات فريدة من أنحاء العالم.', modal_content_en: '', modal_content_ar: '', image_url: '/uploads/8c5b4e6a-a60f-4507-9e40-f95af8bb2444.jpeg', featured: true, enabled: true, order: 4 },
    ],
  },
  about: {
    image_url: '/uploads/7152c341-a5e6-4b26-bc37-d855eeb33ced.jpg',
    values: [
      { id: 'heritage', icon: 'target', title_en: 'Heritage', title_ar: 'التراث', description_en: 'Preserving time-honored traditions while embracing innovation to create timeless products.', description_ar: 'نحافظ على التقاليد العريقة مع تبني الابتكار لصنع منتجات خالدة.', enabled: true, order: 1 },
      { id: 'craftsmanship', icon: 'lightbulb', title_en: 'Craftsmanship', title_ar: 'الحرفية', description_en: 'Every product is meticulously crafted by master artisans with decades of expertise.', description_ar: 'كل منتج مصنوع بدقة على أيدي حرفيين ماهرين بخبرة عقود.', enabled: true, order: 2 },
      { id: 'excellence', icon: 'users', title_en: 'Excellence', title_ar: 'التميز', description_en: 'Committed to delivering nothing but the finest quality to our distinguished clientele worldwide.', description_ar: 'ملتزمون بتقديم أعلى مستويات الجودة لعملائنا المميزين حول العالم.', enabled: true, order: 3 },
    ],
    highlights_en: ['World-class quality standards', 'Advanced manufacturing technology', 'Expert professional team', 'Strong international partnerships'],
    highlights_ar: ['معايير جودة عالمية', 'تقنيات تصنيع متطورة', 'فريق من الخبراء المحترفين', 'شراكات دولية قوية'],
  },
  global: {
    logo_url_dark: '/uploads/f0ff146b-d9ae-4c6c-b8cb-08a79738ee50.png',
    logo_url_light: '/uploads/a76604b9-11dd-4737-a8b8-00479d98f42a.png',
    default_language: 'en',
    default_theme: 'dark',
    menu: DEFAULT_MENU_CONFIG,
  },
};

export const ContentProvider = ({ children }) => {
  const [siteContent, setSiteContent] = useState(FALLBACK_CONTENT);
  const [loading, setLoading] = useState(true);

  const fetchContent = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/content');
      setSiteContent({ ...FALLBACK_CONTENT, ...data });
    } catch (err) {
      console.warn('Could not fetch site content from API, using defaults.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  return (
    <ContentContext.Provider value={{ siteContent, loading, refreshContent: fetchContent }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error('useContent must be used within ContentProvider');
  return ctx;
};
